import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notification, Form, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import stationServicesServices from 'services/stationServicesServices';
import ListSchedulesService from 'services/listSchedulesService';
import ServiceSettingTable from 'Page/Schedule/ServiceSettingTable';
import ServiceSettingModal from 'Page/Schedule/ServiceSettingModal';
import { optionServiceType } from 'constants/setting';
import { number_to_price } from 'helper/common';
import {
  FILTER_BUTTONS,
  PAYMENT_MODES,
  PARENT_SERVICE_TYPES,
  SERVICE_CATEGORY_OPTIONS,
  getDerivedParentServiceType,
} from 'constants/StationServicesConstant';

const normalizeServiceSelection = (items = []) =>
  items
    .filter((item) => item !== undefined && item !== null)
    .map((item) => String(item))
    .sort()
    .join(',');

const buildMetadataServices = (result) => {
  const stationServices = result?.STATION_SERVICE || {};

  return Object.values(stationServices).flatMap((item) => {
    if (item?.serviceType === undefined) {
      return [];
    }

    return [{
      serviceType: item.serviceType,
      serviceName: item.serviceName || '',
      servicePrice: item.servicePrice ?? 0,
      parentServiceType: getDerivedParentServiceType({ serviceType: item.serviceType }),
    }];
  });
};

const buildMetadataServicesFromOptions = (serviceOptions = []) =>
  serviceOptions.map((item) => ({
    serviceType: item.value,
    serviceName: item.label || '',
    servicePrice: item.servicePrice ?? 0,
    parentServiceType: getDerivedParentServiceType({ serviceType: item.value }),
  }));

const mergeStationServices = (metadataServices = [], services = []) => {
  const apiByType = new Map(
    services
      .filter((item) => item?.serviceType !== undefined)
      .map((item) => [item.serviceType, item])
  );
  const metadataServiceTypes = new Set((metadataServices || []).map((item) => item?.serviceType));

  const merged = metadataServices.map((meta) => ({
    serviceType: meta?.serviceType,
    serviceName: meta?.serviceName || '',
    servicePrice: meta?.servicePrice ?? 0,
    serviceBookingFee: meta?.serviceBookingFee ?? meta?.bookingFee ?? 20000,
    ...(apiByType.get(meta?.serviceType) || {}),
  }));

  const stationCustomServices = (services || []).filter((service) => !metadataServiceTypes.has(service?.serviceType));

  return [...merged, ...stationCustomServices];
};

const filterServicesByType = (serviceList = [], filterArray, activeFilter) => {
  if (!filterArray) {
    return serviceList;
  }
  const activeCategory = Number(activeFilter);
  if (activeCategory) {
    return serviceList.filter((item) => getDerivedParentServiceType(item) === activeCategory);
  }
  if (filterArray.length === 0) {
    return serviceList.filter((item) => getDerivedParentServiceType(item) === PARENT_SERVICE_TYPES.OTHER);
  }
  const serviceTypeSet = new Set(filterArray);
  return serviceList.filter((item) => serviceTypeSet.has(item.serviceType));
};

const buildPaymentToggleUpdateData = (service = {}, fieldName, newValue) => {
  const updateData = { [fieldName]: newValue ? 1 : 0 };

  if (fieldName === PAYMENT_MODES.PREPAY.key && newValue === true && service.enableOnlinePayment !== 1) {
    updateData[PAYMENT_MODES.ONLINE.key] = 1;
  }

  if (fieldName === PAYMENT_MODES.ONLINE.key && newValue === false && service.enablePrepay === 1) {
    updateData[PAYMENT_MODES.PREPAY.key] = 0;
  }

  return updateData;
};

const ServiceSetting = () => {
  const { t: translation } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [metadataServices, setMetadataServices] = useState(() => buildMetadataServicesFromOptions(optionServiceType(translation)));
  const [filteredServices, setFilteredServices] = useState([]);
  const [listViewServices, setListViewServices] = useState([]);
  const [filteredListViewServices, setFilteredListViewServices] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [updating, setUpdating] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSavingService, setIsSavingService] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm] = Form.useForm();
  const prevActiveFilterRef = useRef(activeFilter);
  const [dataFilter, setDataFilter] = useState({
    skip: 0,
    limit: 10
  });

  const isListSelectionChanged = useMemo(() => {
    return normalizeServiceSelection(selectedServiceTypes) !== normalizeServiceSelection((services || []).map((item) => item.serviceType));
  }, [selectedServiceTypes, services]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await stationServicesServices.getList({ 
        skip: 0, 
        limit: 100 
      });
      
      setServices(result?.data || []);
    } catch (error) {
      notification.error({
        message: "",
        description: translation('error')
      });
    } finally {
      setLoading(false);
    }
  }, [translation]);

  const fetchMetaData = useCallback(async () => {
    try {
      const result = await ListSchedulesService.getMetaData({});
      const newValues = buildMetadataServices(result || {});
      setMetadataServices(newValues);
    } catch (error) {
      console.error('Fetch metadata error:', error);
    }
  }, []);

  const mergeServices = useCallback(() => {
    setListViewServices(mergeStationServices(metadataServices, services));
  }, [metadataServices, services]);

  useEffect(() => {
    fetchData();
    fetchMetaData();
  }, [fetchData, fetchMetaData]);

  useEffect(() => {
    mergeServices();
  }, [mergeServices]);

  useEffect(() => {
    setSelectedServiceTypes((services || []).map((item) => item.serviceType));
  }, [services]);

  const applyFilter = useCallback(() => {
    const filterButton = FILTER_BUTTONS.find(btn => btn.key === activeFilter);
    const isAll = activeFilter === null || activeFilter === 'all' || !activeFilter;

    if (isAll || !filterButton || filterButton.key === 'all') {
      setFilteredServices(services);
      setFilteredListViewServices(listViewServices);
      return;
    }

    setFilteredServices(filterServicesByType(services, filterButton.filterArray, activeFilter));
    setFilteredListViewServices(filterServicesByType(listViewServices, filterButton.filterArray, activeFilter));
  }, [activeFilter, services, listViewServices]);

  useEffect(() => {
    applyFilter();

    if (prevActiveFilterRef.current !== activeFilter) {
      setDataFilter(prev => ({
        ...prev,
        skip: 0
      }));
      prevActiveFilterRef.current = activeFilter;
    }
  }, [activeFilter, applyFilter]);

  const handleListToggle = (service, isChecked) => {
    setSelectedServiceTypes((prev) => {
      if (isChecked) {
        return prev.includes(service.serviceType) ? prev : [...prev, service.serviceType];
      }
      return prev.filter((serviceType) => serviceType !== service.serviceType);
    });
  };

  const handleSaveListServices = async () => {
    try {
      setLoading(true);
      let error = false;
      const currentServiceTypes = (services || []).map((item) => item.serviceType);
      const addedServiceTypes = selectedServiceTypes.filter((serviceType) => !currentServiceTypes.includes(serviceType));
      const removedServiceTypes = currentServiceTypes.filter((serviceType) => !selectedServiceTypes.includes(serviceType));

      for (let index = 0; index < removedServiceTypes.length; index++) {
        const serviceType = removedServiceTypes[index];
        const service = services.find((item) => item.serviceType === serviceType);
        if (!service?.stationServicesId) {
          continue;
        }
        const response = await stationServicesServices.delete({ id: service.stationServicesId });
        if (!response) {
          error = true;
          break;
        }
      }

      for (let index = 0; index < addedServiceTypes.length; index++) {
        const serviceType = addedServiceTypes[index];
        const service = listViewServices.find((item) => item.serviceType === serviceType);
        if (!service) {
          continue;
        }
        const response = await stationServicesServices.insert({
          serviceType: service.serviceType,
          serviceName: service.serviceName,
          parentServiceType: getDerivedParentServiceType(service),
          servicePrice: Number(service.servicePrice || 0),
          serviceBookingFee: service.serviceBookingFee ?? 20000,
          isActive: 1,
        });
        if (!response) {
          error = true;
          break;
        }
      }

      if (error) {
        notification.error({
          message: "",
          description: translation('scheduleSetting.saveError')
        });
      } else {
        notification.success({
          message: "",
          description: translation('scheduleSetting.saveSuccess')
        });
      }
      await fetchData();
    } catch (error) {
      notification.error({
        message: "",
        description: error.message || translation('error')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (pageNum) => {
    const newFilter = {
      ...dataFilter,
      skip: (pageNum - 1) * dataFilter.limit
    };
    setDataFilter(newFilter);
  };

  const handlePriceChange = async (service, newPrice) => {
    try {
      setUpdating(prev => ({
        ...prev,
        [service.serviceType]: true
      }));

      const priceValue = Number(newPrice);

      if (priceValue !== 0 && priceValue < 1000) {
        notification.error({
          message: "",
          description: translation('setting.service.price.invalid')
        });
        setUpdating(prev => {
          const newUpdating = { ...prev };
          delete newUpdating[service.serviceType];
          return newUpdating;
        });
        return;
      }

      const updateData = { servicePrice: priceValue };


      if (priceValue === 0) {
        updateData.enableOnlinePayment = 0;
        updateData.enablePaymentCollection = 0;
        updateData.enablePrepay = 0;
      }

      if (!service?.stationServicesId) {
        notification.warning({
          message: "",
          description: "Dịch vụ không tồn tại"
        });
        return;
      }

      const apiResponse = await stationServicesServices.updateById({
        id: service.stationServicesId,
        data: updateData
      });

      if (apiResponse) {
        notification.success({
          message: "",
          description: translation('updateSuccess')
        });

        await fetchData();
      } else {
        notification.error({
          message: "",
          description: translation('updateFailed')
        });
      }
    } catch (error) {
      notification.error({
        message: "",
        description: error.message || translation('error')
      });
    } finally {
      setUpdating(prev => {
        const newUpdating = { ...prev };
        delete newUpdating[service.serviceType];
        return newUpdating;
      });
    }
  };

  const handleToggle = async (service, fieldName, newValue) => {
    try {
      setUpdating(prev => ({
        ...prev,
        [service.serviceType]: true
      }));

      const updateData = buildPaymentToggleUpdateData(service, fieldName, newValue);
      if (!service?.stationServicesId) {
        notification.warning({
          message: "",
          description: "Dịch vụ không tồn tại"
        });
        return;
      }

      const apiResponse = await stationServicesServices.updateById({
        id: service.stationServicesId,
        data: updateData
      });

      if (apiResponse) {
        notification.success({
          message: "",
          description: translation('updateSuccess')
        });
        
        await fetchData();
      } else {
        notification.error({
          message: "",
          description: translation('updateFailed')
        });
      }
    } catch (error) {
      notification.error({
        message: "",
        description: error.message || translation('error')
      });
    } finally {
      setUpdating(prev => {
        const newUpdating = { ...prev };
        delete newUpdating[service.serviceType];
        return newUpdating;
      });
    }
  };

  const serviceCategoryOptions = SERVICE_CATEGORY_OPTIONS;

  const openAddModal = () => {
    setEditingService(null);
    serviceForm.setFieldsValue({
      serviceName: '',
      parentServiceType: activeFilter !== 'all' ? activeFilter : undefined,
      servicePrice: number_to_price(0),
      bookingFee: number_to_price(20000),
    });
    setIsModalOpen(true);
  };

  const openEditModal = (service) => {
    if (!service?.stationServicesId) {
      return;
    }

    setEditingService(service);
    serviceForm.setFieldsValue({
      serviceName: service.serviceName,
      parentServiceType: getDerivedParentServiceType(service),
      servicePrice: number_to_price(service.servicePrice || 0),
      bookingFee: number_to_price(service.serviceBookingFee ?? 20000),
    });
    setIsModalOpen(true);
  };

  const handleSaveService = async (payload) => {
    if (isSavingService) {
      return;
    }

    try {
      setIsSavingService(true);
      const current = editingService || {};

      const response = current?.stationServicesId
        ? await stationServicesServices.updateById({
          id: current.stationServicesId,
          data: payload,
        })
        : await stationServicesServices.insert({
          ...payload,
          isActive: 1,
        });

      if (response) {
        notification.success({
          message: "",
          description: current?.stationServicesId ? translation('updateSuccess') : translation('createSuccess')
        });
        setIsModalOpen(false);
        await fetchData();
      } else {
        notification.error({
          message: "",
          description: translation('updateFailed')
        });
      }
    } catch (error) {
    } finally {
      setIsSavingService(false);
    }
  };

  const handleDeleteService = async (service) => {
    if (!service?.stationServicesId) {
      notification.warning({
        message: "",
        description: "Dịch vụ không tồn tại"
      });
      return;
    }
    const response = await stationServicesServices.delete({ id: service.stationServicesId });
    if (response) {
      notification.success({
        message: "",
        description: "Xóa thành công"
      });
      await fetchData();
    } else {
      notification.error({
        message: "",
        description: translation('updateFailed')
      });
    }
  };

  return (
    <Row>
      <Col span={24}>
        <ServiceSettingTable
          filteredServices={filteredServices}
          listViewServices={viewMode === 'list' ? listViewServices : filteredListViewServices}
          selectedServiceTypes={selectedServiceTypes}
          dataFilter={dataFilter}
          activeFilter={activeFilter}
          viewMode={viewMode}
          updating={updating}
          loading={loading}
          handleChangePage={handleChangePage}
          handlePriceChange={handlePriceChange}
          handleToggle={handleToggle}
          handleListToggle={handleListToggle}
          onSaveListServices={handleSaveListServices}
          isListSelectionChanged={isListSelectionChanged}
          onFilterChange={setActiveFilter}
          onViewModeChange={setViewMode}
          onAdd={openAddModal}
          onEdit={openEditModal}
          onDelete={handleDeleteService}
        />
      </Col>
      <ServiceSettingModal
        visible={isModalOpen}
        editingService={editingService}
        form={serviceForm}
        services={services}
        serviceCategoryOptions={serviceCategoryOptions}
        confirmLoading={isSavingService}
        onCancel={() => setIsModalOpen(false)}
        onSave={handleSaveService}
      />
    </Row>
  );
};

export default ServiceSetting;
